import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Container, Form, Modal, Spinner, Table } from "react-bootstrap";
import API from "../../services/api";
import "../EmployeeAccounts.css";

const orDash = (v) => (v === 0 ? "0" : v == null || String(v).trim() === "" ? "—" : v);

const HrEmployeesManage = () => {
  const [rows, setRows] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [form, setForm] = useState({});
  const [editForm, setEditForm] = useState({});

  const fetchAll = async () => {
    try {
      setLoading(true);
      setErr("");
      const [empRes, deptRes, roleRes] = await Promise.all([
        API.get("/employee/hr/employees"),
        API.get("/employee/hr/departments"),
        API.get("/employee/hr/roles"),
      ]);
      setRows(empRes.data || []);
      setDepartments(deptRes.data || []);
      setRoles(roleRes.data || []);
    } catch (e) {
      setErr(e?.response?.data?.message || "Failed to load employees");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const openCreate = () => {
    setForm({ fullName: "", mobile: "", email: "", password: "", departmentId: "", roleId: "", approved: false });
    setShowCreate(true);
  };

  const submitCreate = async (e) => {
    e.preventDefault();
    try {
      await API.post("/employee/hr/employees", form);
      setShowCreate(false);
      fetchAll();
    } catch (e1) {
      setErr(e1?.response?.data?.message || "Create failed");
    }
  };

  const openEdit = (row) => {
    setEditForm({
      id: row._id,
      fullName: row.fullName || "",
      mobile: row.mobile || "",
      email: row.email || "",
      password: "",
      departmentId: row.department?._id || "",
      roleId: row.employeeRole?._id || "",
      approved: !!row.approved,
    });
    setShowEdit(true);
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...editForm };
      delete payload.id;
      if (!payload.password) delete payload.password;
      await API.put(`/employee/hr/employees/${editForm.id}`, payload);
      setShowEdit(false);
      fetchAll();
    } catch (e1) {
      setErr(e1?.response?.data?.message || "Update failed");
    }
  };

  const filteredRoles = useMemo(() => {
    if (!editForm.departmentId && !form.departmentId) return roles;
    const deptId = editForm.departmentId || form.departmentId;
    return roles.filter((r) => String(r.department?._id || r.department) === String(deptId));
  }, [roles, editForm.departmentId, form.departmentId]);

  return (
    <Container className="mt-4 accounts-page">
      <div className="accounts-header">
        <div>
          <div className="accounts-title-row">
            <h4 className="mb-0">HR Employee Directory</h4>
            <span className="accounts-count">{rows.length} total</span>
          </div>
          <p className="accounts-subtitle">Create, assign department/role, and activate employees.</p>
        </div>
        <div className="accounts-actions">
          <Button variant="outline-secondary" size="sm" onClick={fetchAll}>
            Refresh
          </Button>
          <Button variant="primary" size="sm" onClick={openCreate}>
            Add Employee
          </Button>
        </div>
      </div>

      {err && <Alert variant="danger">{err}</Alert>}

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <Spinner animation="border" />
        </div>
      ) : (
        <div className="accounts-table-card">
          <Table hover responsive className="accounts-table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Email</th>
                <th>Mobile</th>
                <th>Department</th>
                <th>Role</th>
                <th>Approved</th>
                <th className="text-end">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center">No employees found</td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={r._id}>
                    <td>{orDash(r.fullName)}</td>
                    <td>{orDash(r.email)}</td>
                    <td>{orDash(r.mobile)}</td>
                    <td>{orDash(r.department?.name)}</td>
                    <td>{orDash(r.employeeRole?.name)}</td>
                    <td>{r.approved ? "Yes" : "No"}</td>
                    <td className="text-end">
                      <Button size="sm" variant="outline-primary" onClick={() => openEdit(r)}>
                        Edit
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
      )}

      <Modal show={showCreate} onHide={() => setShowCreate(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add Employee</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={submitCreate}>
            <Form.Group className="mb-3">
              <Form.Label>Full Name</Form.Label>
              <Form.Control
                value={form.fullName || ""}
                onChange={(e) => setForm({ ...form, fullName: e.target.value })}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Mobile</Form.Label>
              <Form.Control
                value={form.mobile || ""}
                onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                value={form.email || ""}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Password</Form.Label>
              <Form.Control
                type="password"
                value={form.password || ""}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Department</Form.Label>
              <Form.Select
                value={form.departmentId || ""}
                onChange={(e) => setForm({ ...form, departmentId: e.target.value })}
              >
                <option value="">Select</option>
                {departments.map((d) => (
                  <option key={d._id} value={d._id}>{d.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Role</Form.Label>
              <Form.Select
                value={form.roleId || ""}
                onChange={(e) => setForm({ ...form, roleId: e.target.value })}
              >
                <option value="">Select</option>
                {filteredRoles.map((r) => (
                  <option key={r._id} value={r._id}>{r.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Check
              className="mb-3"
              type="checkbox"
              label="Approved (Active)"
              checked={!!form.approved}
              onChange={(e) => setForm({ ...form, approved: e.target.checked })}
            />
            <div className="d-flex justify-content-end gap-2">
              <Button variant="outline-secondary" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button type="submit" variant="primary">Save</Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      <Modal show={showEdit} onHide={() => setShowEdit(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Employee</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={submitEdit}>
            <Form.Group className="mb-3">
              <Form.Label>Full Name</Form.Label>
              <Form.Control
                value={editForm.fullName || ""}
                onChange={(e) => setEditForm({ ...editForm, fullName: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Mobile</Form.Label>
              <Form.Control
                value={editForm.mobile || ""}
                onChange={(e) => setEditForm({ ...editForm, mobile: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                value={editForm.email || ""}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Reset Password</Form.Label>
              <Form.Control
                type="password"
                value={editForm.password || ""}
                onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                placeholder="Leave blank to keep"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Department</Form.Label>
              <Form.Select
                value={editForm.departmentId || ""}
                onChange={(e) => setEditForm({ ...editForm, departmentId: e.target.value })}
              >
                <option value="">Select</option>
                {departments.map((d) => (
                  <option key={d._id} value={d._id}>{d.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Role</Form.Label>
              <Form.Select
                value={editForm.roleId || ""}
                onChange={(e) => setEditForm({ ...editForm, roleId: e.target.value })}
              >
                <option value="">Select</option>
                {filteredRoles.map((r) => (
                  <option key={r._id} value={r._id}>{r.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Check
              className="mb-3"
              type="checkbox"
              label="Approved (Active)"
              checked={!!editForm.approved}
              onChange={(e) => setEditForm({ ...editForm, approved: e.target.checked })}
            />
            <div className="d-flex justify-content-end gap-2">
              <Button variant="outline-secondary" onClick={() => setShowEdit(false)}>Cancel</Button>
              <Button type="submit" variant="primary">Save</Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default HrEmployeesManage;
